import React, { useCallback, useEffect, useState } from 'react';
import { getReport, publishGame, unpublishGame } from '../api/gameBuilderAPI';

/**
 * Sharing a built game with learners: publish it (frozen as it is now), copy the guest link (/play/<token>, no
 * login), and see how the guests did (backend services/game_builder/publish.py). Republishing makes a new version
 * behind the same link; changes made after publishing reach learners only when published again.
 */
const linkOf = (token) => `${window.location.origin}/play/${token}`;
const timeOf = (s) => (s == null ? '-' : s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`);
const OUTCOME = { goal: '✅ finished', fail: '❌ went wrong', dead_end: '❌ stuck', left: '… left' };

// `quiet`: something else is the call to action for now (play-test it first), so Publish waits its turn
export default function PublishPanel({ planId, missionId, quiet = false }) {
  const [report, setReport] = useState(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try { setReport(await getReport(planId, missionId)); } catch (e) { setError(e.message); }
  }, [planId, missionId]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn) => {
    setWorking(true); setError('');
    try { await fn(planId, missionId); await load(); } catch (e) { setError(e.message); } finally { setWorking(false); }
  };
  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (e) { /* the link is selectable */ }
  };

  if (!report) return error ? <div className="gb-error" role="alert"><span>{error}</span></div> : <p className="gb-muted gb-small">Loading…</p>;
  const pub = report.published;
  return (
    <section className="gb-panel gb-publish">
      <h4>Share with learners</h4>
      {pub ? (
        <>
          <div className="gb-publish-link">
            <input type="text" readOnly value={linkOf(pub.token)} aria-label="Guest link" onFocus={(e) => e.target.select()} />
            <button type="button" className="gb-btn gb-btn-sm" onClick={() => copy(linkOf(pub.token))}>{copied ? 'Copied ✓' : 'Copy link'}</button>
          </div>
          <p className="gb-small gb-muted">
            Version {pub.version}, published {new Date(pub.published_at).toLocaleString()}. Anyone with the link can play - no login.
            Changes you make now reach learners when you publish again.
          </p>
          <div className="gb-actions">
            <button type="button" className="gb-btn gb-btn-quiet" disabled={working} onClick={() => act(publishGame)}>Publish again</button>
            <button type="button" className="gb-link" disabled={working} onClick={() => act(unpublishGame)}>Stop sharing</button>
          </div>
        </>
      ) : (
        <>
          <p className="gb-small">Publishing freezes the game as it is now and gives you a link learners open without logging in.</p>
          <button type="button" className={`gb-btn ${quiet ? 'gb-btn-quiet' : ''}`} disabled={working} onClick={() => act(publishGame)}>
            {working ? 'Publishing…' : 'Publish'}
          </button>
        </>
      )}
      {error && <div className="gb-error" role="alert"><span>{error}</span></div>}

      {report.attempts > 0 && (
        <>
          <h4>How learners did</h4>
          <div className="gb-report-stats">
            <span><strong>{report.attempts}</strong> plays</span>
            <span><strong>{report.guests}</strong> learners</span>
            <span><strong>{report.won}</strong> finished right</span>
            <span><strong>{timeOf(report.median_seconds)}</strong> usual time</span>
          </div>
          {report.mistakes.length > 0 && (
            <>
              <p className="gb-small"><strong>Most common mistakes</strong></p>
              <ul className="gb-small">{report.mistakes.map((m) => <li key={m.name}>{m.name} - {m.count}×</li>)}</ul>
            </>
          )}
          <p className="gb-small"><strong>Latest plays</strong></p>
          <ul className="gb-small">
            {report.recent.map((r) => (
              <li key={r.at}>{new Date(r.at).toLocaleString()} · {r.name || 'Guest'} · {OUTCOME[r.outcome] || r.outcome} · {timeOf(r.seconds)}
                {r.wrong ? ` · ${r.wrong} wrong` : ''}{r.hints ? ` · ${r.hints} hint(s)` : ''}</li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
